/**
 * <anja-tabs> — upgrades a row of links into an accessible tablist (WAI-ARIA tabs pattern).
 *
 * Markup contract (server-rendered, works without JS as plain links):
 *   [data-anja-tablist] > [data-anja-tab][data-panel="<panel id>"]
 *   [data-anja-panel]#<panel id>  (inactive panels may hold <template data-anja-panel-template>)
 *   [data-anja-tab-link] (optional "Shop all" link that follows the active panel's data-url)
 *
 * Panels from <template> are materialised once, on first activation, so hidden tabs never
 * download images up front. Instances are independent; listeners are removed on disconnect so
 * the theme editor can re-render, duplicate and reorder sections safely.
 */
class AnjaTabs extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    const list = this.querySelector(':scope [data-anja-tablist]');
    if (list && !list.querySelector('[data-anja-tab]')) this.#buildTabs(list);
    this.tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-anja-tab]')));
    if (!list || this.tabs.length < 2) return;
    list.hidden = false;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    list.setAttribute('role', 'tablist');
    this.tabs.forEach((tab) => {
      const panel = this.#panelFor(tab);
      tab.setAttribute('role', 'tab');
      if (panel) {
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
      }
      tab.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.select(tab);
        },
        { signal }
      );
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, tab), { signal });
    });

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const target = /** @type {Node} */ (event.target);
        const tab = this.tabs.find(
          (candidate) => candidate === target || this.#panelFor(candidate)?.contains(target) || target.contains?.(this.#panelFor(candidate))
        );
        if (tab) this.select(tab, false);
      },
      { signal }
    );

    const initial = this.tabs.find((tab) => tab.hasAttribute('data-active')) ?? this.tabs[0];
    this.select(initial, false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * Creates one tab per panel from the panel's data-label. Used when each tab is a theme block that
   * renders its own panel, so the server cannot render the tab bar in order.
   * @param {Element} list
   */
  #buildTabs(list) {
    const panels = Array.from(this.querySelectorAll('[data-anja-panel][data-label]')).filter(
      (panel) => panel.closest('anja-tabs') === this
    );
    if (panels.length < 2) return;
    for (const panel of panels) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'anja-tabs__tab';
      tab.id = `AnjaTab-${panel.id}`;
      tab.dataset.anjaTab = '';
      tab.dataset.panel = panel.id;
      tab.textContent = /** @type {HTMLElement} */ (panel).dataset.label || '';
      list.append(tab);
    }
  }

  /** @param {HTMLElement} tab */
  #panelFor(tab) {
    const id = tab.dataset.panel;
    return id ? /** @type {HTMLElement | null} */ (this.querySelector(`#${CSS.escape(id)}`)) : null;
  }

  /**
   * @param {HTMLElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = true) {
    for (const candidate of this.tabs) {
      const active = candidate === tab;
      candidate.setAttribute('aria-selected', String(active));
      candidate.tabIndex = active ? 0 : -1;
      candidate.toggleAttribute('data-active', active);
      const panel = this.#panelFor(candidate);
      if (!panel) continue;
      if (active) this.#materialise(panel);
      panel.hidden = !active;
    }

    const panel = this.#panelFor(tab);
    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-anja-tab-link]'));
    if (link && panel?.dataset.url) link.href = panel.dataset.url;

    if (focus) tab.focus();
    this.dispatchEvent(new CustomEvent('anja-tabs:change', { bubbles: true, detail: { tab, panel } }));
  }

  /** @param {HTMLElement} panel */
  #materialise(panel) {
    const template = panel.querySelector(':scope > template[data-anja-panel-template]');
    if (!(template instanceof HTMLTemplateElement)) return;
    panel.append(template.content.cloneNode(true));
    template.remove();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement} tab
   */
  #onKeydown(event, tab) {
    const rtl = getComputedStyle(this).direction === 'rtl';
    const index = this.tabs.indexOf(tab);
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = rtl ? index - 1 : index + 1;
        break;
      case 'ArrowLeft':
        next = rtl ? index + 1 : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const count = this.tabs.length;
    const target = this.tabs[(next + count) % count];
    if (target) this.select(target);
  }
}

if (!customElements.get('anja-tabs')) customElements.define('anja-tabs', AnjaTabs);
